<template>
  <div class="col-12">
    <vue-headful :title="`Booking Requests • ${$store.state.settings.settings.site_name}`" />
    <h1>Booking Requests</h1>
    <div :class="$style.container">
      <div class="row">
        <div
          :class="$style.detailContainer">
          <card
            class="mb-3 card-form">
            <template slot="header">
              <h3
                class="mb-0"
                :class="$style.bookingTitle">
                {{ bookingTitle }}
              </h3>
            </template>
            <div v-if="selectedBooking">
              <div class="row mb-3">
                <div class="col-6" />
                <div class="col-6 text-right">
                  <label>Request Date</label>
                  {{ formatDate(selectedBooking.created_at) }}
                </div>
              </div>
              <div :class="$style.form">
                <div class="row">
                  <div class="col-6">
                    <label>Email</label>
                    <span>{{ selectedBooking.email }}</span>
                  </div>
                  <div class="col-6">
                    <label>Phone</label>
                    <span> {{ selectedBooking.phone }}</span>
                  </div>
                </div>
                <div class="row">
                  <div class="col-6">
                    <label>From</label>
                    <el-date-picker
                      :clearable="false"
                      format="M/dd/yy"
                      v-model="fromDate"
                      type="date"
                      placeholder="Select date" />
                  </div>
                  <div class="col-6">
                    <label>To</label>
                    <el-date-picker
                      :clearable="false"
                      :picker-options="{ disabledDate: isToDateSelectionInvalid }"
                      format="M/dd/yy"
                      v-model="toDate"
                      type="date"
                      placeholder="Select date" />
                  </div>
                </div>
                <div class="row">
                  <div class="col-6">
                    <label>Adventure</label>
                    <el-select
                      class="select-primary"
                      filterable
                      v-model="adventureType"
                      placeholder="Select adventure type">
                      <el-option
                        class="select-primary"
                        v-for="type in adventureTypes"
                        :key="type.value"
                        :label="type.label"
                        :value="type.value" />
                    </el-select>
                  </div>
                  <div class="col-6">
                    <label>Participants</label>
                    <el-input-number
                      v-model="numberOfParticipants"
                      :min="0" />
                  </div>
                </div>
                <div
                  v-if="showHuntOptions"
                  class="row">
                  <div class="col-6">
                    <label>Hunt Type</label>
                    <el-select
                      class="select-primary"
                      filterable
                      v-model="huntType"
                      placeholder="Select hunt type">
                      <el-option
                        class="select-primary"
                        v-for="type in huntTypes"
                        :key="type.value"
                        :label="type.label"
                        :value="type.value" />
                    </el-select>
                  </div>
                  <div class="col-6">
                    <label>Hunt Species</label>
                    <el-select
                      class="select-primary"
                      filterable
                      v-model="huntSpecies"
                      placeholder="Select hunt species">
                      <el-option
                        class="select-primary"
                        v-for="type in huntSpeciesList"
                        :key="type.value"
                        :label="type.label"
                        :value="type.value" />
                    </el-select>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12">
                    <label>Notes</label>
                    <textarea
                      style="max-height: unset;"
                      v-model="bookingNotes"
                      class="form-control"
                      rows="2" />
                  </div>
                </div>
                <div
                  v-if="bookedAdventure"
                  class="row mb-0">
                  <div class="col-4">
                    <label>Paid on</label>
                    <el-date-picker
                      format="M/dd/yy"
                      v-model="paidDate"
                      type="date"
                      placeholder="Unpaid" />
                  </div>
                  <div class="col-8 text-right">
                    <base-button
                      type="danger"
                      size="sm"
                      class="btn-fill"
                      @click="cancelAdventure">
                      Cancel Adventure
                    </base-button>
                  </div>
                </div>
                <div
                  v-else
                  class="row mb-0">
                  <div
                    class="col-12 text-right">
                    <base-button
                      type="primary"
                      size="sm"
                      class="btn-fill"
                      @click="bookAdventure">
                      Book Adventure
                    </base-button>
                  </div>
                </div>
              </div>
            </div>
            <div v-else>
              Select a booking from the calendar or from the list below.
            </div>
          </card>
          <card class="mb-0 card-form unbookedRequestsCard">
            <template slot="header">
              <div class="flex-between">
                <h4 class="mb-2">Unbooked Requests</h4>
                <div :class="$style.legend">
                  <div :class="$style.item">
                    <div :class="[$style.canceled, $style.colorBox]" />
                    Canceled
                  </div>
                </div>
              </div>
            </template>
            <el-table
              height="255"
              stripe
              highlight-current-row
              :data="unbookedRequests"
              :row-class-name="tableRowClassName"
              @row-click="editBookingRequest">
              <el-table-column
                v-for="column in tableColumns"
                :key="column.label"
                :min-width="column.minWidth"
                :width="column.width"
                :prop="column.prop"
                :label="column.label"
                :formatter="column.formatter"
                :show-overflow-tooltip="true" />
            </el-table>
          </card>
        </div>
        <calendar-view
          :items="calendarItems"
          :show-date="showDate"
          :enable-date-selection="true"
          :selection-start="selectionStart"
          :selection-end="selectionEnd"
          :enable-drag-drop="true"
          @date-selection="dateSelection"
          @date-selection-finish="dateSelectionEnd"
          @click-date="clickedDate"
          @click-item="editBookingRequest"
          @drop-on-date="movedBooking"
          class="theme-default holiday-us-traditional holiday-us-official"
          :class="$style.calendar">
          <calendar-view-header
            slot="header"
            slot-scope="t"
            :header-props="t.headerProps"
            @input="setShowDate" />
        </calendar-view>
      </div>
    </div>
  </div>
</template>
<script>
import moment from 'moment-timezone';
import _ from 'lodash';

import { CalendarView, CalendarViewHeader, CalendarMathMixin } from 'vue-simple-calendar';
import { DatePicker, InputNumber } from 'element-ui';

require('vue-simple-calendar/static/css/default.css');
require('vue-simple-calendar/static/css/holidays-us.css');

export default {
  name: 'BookingRequests',
  components: {
    CalendarView,
    CalendarViewHeader,
    [DatePicker.name]: DatePicker,
    [InputNumber.name]: InputNumber,
  },
  mixins: [CalendarMathMixin],
  props: {
    bookingRequests: {
      type: Array,
      required: true,
    },
    adventureTypes: {
      type: Array,
      required: true,
    },
    huntTypes: {
      type: Array,
      required: true,
    },
    huntSpeciesList: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      showDate: new Date(),
      selectionStart: null,
      selectionEnd: null,
      selectedBooking: null,
      tableColumns: [
        {
          prop: 'created_at',
          label: 'Requested',
          width: 110,
          formatter: row => this.formatDate(row.created_at),
        },
        {
          prop: 'full_name',
          label: 'Customer',
        },
        {
          prop: 'adventure_type',
          label: 'Adventure',
          formatter: row => this.getAdventureLabel(row.adventure_type),
        },
      ],
    };
  },
  computed: {
    calendarItems() {
      return this.bookingRequests
        .filter(booking => booking.status === 'booked')
        .map(booking => ({
          id: booking.id,
          title: `${booking.first_name} - ${this.getAdventureLabel(booking.adventure_type)} - ${booking.number_of_participants}`,
          startDate: booking.from_date,
          endDate: booking.to_date,
          classes: this.classesForBookingRequest(booking),
        }));
    },
    unbookedRequests() {
      return this.bookingRequests
        .filter(booking => booking.status !== 'booked');
    },
    showHuntOptions() {
      return this.selectedBooking.adventure_type === 'hunt';
    },
    fromDate: {
      get() {
        return this.selectedBooking.from_date;
      },
      set(newValue) {
        this.$emit('updateBooking', { id: this.selectedBooking.id, from_date: newValue });
      },
    },
    toDate: {
      get() {
        return this.selectedBooking.to_date;
      },
      set(newValue) {
        this.$emit('updateBooking', { id: this.selectedBooking.id, to_date: newValue });
      },
    },
    adventureType: {
      get() {
        return this.selectedBooking.adventure_type;
      },
      set(newValue) {
        this.$emit('updateBooking', { id: this.selectedBooking.id, adventure_type: newValue });
      },
    },
    numberOfParticipants: {
      get() {
        return this.selectedBooking.number_of_participants;
      },
      set(newValue) {
        // For some reason this is triggered twice when updating...eliminate duplicate changes
        if (newValue !== this.selectedBooking.number_of_participants) {
          this.$emit('updateBooking', { id: this.selectedBooking.id, number_of_participants: newValue });
        }
      },
    },
    huntType: {
      get() {
        return this.selectedBooking.hunt_type;
      },
      set(newValue) {
        this.$emit('updateBooking', { id: this.selectedBooking.id, hunt_type: newValue });
      },
    },
    huntSpecies: {
      get() {
        return this.selectedBooking.hunt_species;
      },
      set(newValue) {
        this.$emit('updateBooking', { id: this.selectedBooking.id, hunt_species: newValue });
      },
    },
    bookingNotes: {
      get() {
        return this.selectedBooking.request_notes;
      },
      set: _.debounce(function set(newValue) {
        this.$emit('updateBooking', { id: this.selectedBooking.id, request_notes: newValue });
      }, 500, { trailing: true }),
    },
    paidDate: {
      get() {
        return this.selectedBooking.paid_date;
      },
      set(newValue) {
        this.$emit('updateBooking', { id: this.selectedBooking.id, paid_date: newValue });
      },
    },
    bookedAdventure() {
      return this.selectedBooking.status === 'booked';
    },
    bookingTitle() {
      if (this.selectedBooking) {
        return `${this.selectedBooking.full_name} - ${this.getAdventureLabel(this.selectedBooking.adventure_type)}`;
      }

      return 'No booking request selected';
    },
  },
  methods: {
    classesForBookingRequest(booking) {
      const styles = [];
      if (booking.paid_date) {
        styles.push(this.$style.paid);
      } else {
        styles.push(this.$style.unpaid);
      }

      const now = new Date().setHours(0, 0, 0);
      const toDate = new Date(booking.to_date).setHours(23, 59, 59);
      if (toDate < now) {
        styles.push(this.$style.past);
      }

      return styles;
    },
    tableRowClassName({ row }) {
      if (row.status === 'canceled') {
        return this.$style.canceled;
      }
      return '';
    },
    isToDateSelectionInvalid(date) {
      return date < new Date(this.selectedBooking.from_date);
    },
    formatDate(date) {
      if (date) {
        return moment(date).format('M/DD/YY');
      }
      return '--';
    },
    getAdventureLabel(value) {
      return this.adventureTypes.find(type => type.value === value).label;
    },
    setShowDate(date) {
      this.showDate = date;
    },
    dateSelection([start, end]) {
      this.selectionStart = start;
      this.selectionEnd = end;
    },
    dateSelectionEnd() {
      this.selectionStart = null;
      this.selectionEnd = null;
    },
    movedBooking(booking, date) {
      // Determine the delta between the old start date and the date chosen,
      // and apply that delta to both the start and end date to move the item.
      const eLength = this.dayDiff(booking.startDate, date);
      const newStartDate = this.addDays(booking.startDate, eLength);
      const newEndDate = this.addDays(booking.endDate, eLength);

      this.$emit('updateBooking', { id: booking.id, from_date: newStartDate, to_date: newEndDate });
    },
    clickedDate() {
      // console.log(date);
    },
    editBookingRequest(booking) {
      this.selectedBooking = this.bookingRequests.find(r => r.id === booking.id);
    },
    bookAdventure() {
      this.$emit('updateBooking', { id: this.selectedBooking.id, status: 'booked' });
    },
    cancelAdventure() {
      this.$emit('updateBooking', { id: this.selectedBooking.id, status: 'canceled' });
    },
  },
  watch: {
    bookingRequests(newValue) {
      if (this.selectedBooking) {
        this.selectedBooking = newValue.find(booking => booking.id === this.selectedBooking.id);
      }
    },
  },
};
</script>

<style>
.unbookedRequestsCard .card-body {
  padding-bottom: 10px;
  padding-top: 0px
}
</style>

<style module lang="scss">
@import 'assets/sass/dashboard/custom/_booking_requests.scss';
.calendar .paid {
  background: $paid;
  color: white;
}
.calendar .unpaid {
  background: $unpaid;
  color: white;
}

.calendar .paid.past {
  background: $paid-past;

}
.calendar .unpaid.past {
  background: $unpaid-past;
}

.canceled {
  background: $canceled!important;
}
.calendar {
  min-height: 780px!important;
}
.container {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}
.detailContainer {
  width: 480px;
  padding-right: 10px;
  display: flex;
  flex-direction: column;
}
.form label {
  display: block;
}
.form > div {
  margin-bottom: 20px;
}
.bookingTitle {
  font-size: 1.3rem
}
.legend {
  display: flex;
}
.item {
  display: flex;
  padding-right: 20px;
}
.colorBox {
  width: 20px;
  height: 20px;
  display: inline-flex;
  margin-right: 10px;
  border: 1px solid gray;
}
</style>
